import { useId } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Badge, CircularLoader } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { StatusChip } from "app/pages/sales-intelligence/common-components/StatusChip/StatusChip";
import { commonKeys } from "app/pages/sales-intelligence/utils/i18n/common";
import dateTimeService from "app/services/date-time/dateTimeService";

import { DEAL_SIGNAL_DATE_FORMAT } from "../../constants";
import type { SignalRecommenderFile } from "../../types/model";
import {
    isDealSignalFailed,
    isDealSignalStalled,
    isDealSignalSucceeded,
    isDealSignalActivelyProcessing,
} from "../../utils/dealSignalStatus";
import { formatDealSignalElapsed } from "../../utils/formatDealSignalElapsed";
import signalRecommenderI18nKeys from "../../utils/i18n";

import { DealSignalFileAction } from "./DealSignalFileAction";
import { DealSignalFileMetric } from "./DealSignalFileMetric";
import {
    ENRICHING_LOADER_OPTIONS,
    StyledEnrichingBody,
    StyledEnrichingChip,
    StyledFileDate,
    StyledFileIcon,
    StyledFileName,
    StyledFileRow,
    StyledIndeterminateBar,
    StyledMetaCell,
} from "./styles";

type DealSignalFileItemProps = {
    readonly file: SignalRecommenderFile;
    readonly isDownloading: boolean;
    readonly isRefreshing: boolean;
    readonly onDownload: (file: SignalRecommenderFile) => void;
    readonly onRefresh: (file: SignalRecommenderFile) => void;
};

export const DealSignalFileItem = ({
    file,
    isDownloading,
    isRefreshing,
    onDownload,
    onRefresh,
}: DealSignalFileItemProps) => {
    const { translate } = useTranslate();

    const statusId = useId();

    const { isDownloaded, status } = file;

    // A stalled run is still in progress on the wire, so it is neither a success nor a reported
    // failure: it takes the amber in-progress chip rather than the red one.
    const isStalled = isDealSignalStalled(file);
    const isProcessing = isDealSignalActivelyProcessing(file);
    const isSucceeded = isDealSignalSucceeded(file);
    const isFailed = isDealSignalFailed(file);

    const statusKey = isStalled ? signalRecommenderI18nKeys.list.status.stalled : commonKeys.failed;

    // Only a finished, successful result can be new — there is nothing to have been read otherwise.
    const isNew = !isDownloaded && isSucceeded;

    // Stalled keeps the pending label: the page has stopped watching, but the backend may still land a
    // result, so the metrics are unknown rather than confirmed missing. An actively enriching row never
    // reaches the metric cells at all — it renders its own layout below.
    const metricEmptyLabel = isStalled
        ? signalRecommenderI18nKeys.list.metric.pending
        : signalRecommenderI18nKeys.list.metric.unavailable;

    const elapsed = formatDealSignalElapsed(file.created, translate);

    const formattedDate = dateTimeService.formatWithUTCMoment(file.created, DEAL_SIGNAL_DATE_FORMAT);

    // A run with no time of day on `created` cannot be measured in elapsed terms, so it falls back to
    // the absolute date — a separate sentence rather than a swapped-in fragment, so both read whole.
    const startedLine = elapsed
        ? signalRecommenderI18nKeys.list.startedAgo({ elapsed })
        : signalRecommenderI18nKeys.list.startedOn({ date: formattedDate });

    if (isProcessing) {
        return (
            <StyledFileRow data-automation="signal-recommender-file-item">
                <StyledEnrichingBody>
                    <StyledFileIcon
                        iconName="csv-file"
                        size="sm"
                    />
                    <Stack
                        flex="1"
                        gap="8px"
                        overflow="hidden"
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            gap="12px"
                            overflow="hidden"
                        >
                            <StyledFileName>{file.fileName}</StyledFileName>

                            <StyledEnrichingChip
                                aria-live="polite"
                                data-automation="signal-recommender-file-status"
                            >
                                <CircularLoader options={ENRICHING_LOADER_OPTIONS} />
                                {translate(signalRecommenderI18nKeys.list.status.enriching)}
                            </StyledEnrichingChip>
                        </Flex>

                        <StyledIndeterminateBar
                            role="progressbar"
                            aria-label={translate(signalRecommenderI18nKeys.list.status.enriching)}
                            data-automation="signal-recommender-file-progress"
                        />

                        <StyledFileDate data-automation="signal-recommender-file-started">
                            {translate(startedLine.key, startedLine.replacements)}
                        </StyledFileDate>
                    </Stack>
                </StyledEnrichingBody>
            </StyledFileRow>
        );
    }

    return (
        <StyledFileRow data-automation="signal-recommender-file-item">
            <Flex
                alignItems="center"
                overflow="hidden"
            >
                <StyledFileIcon
                    iconName="csv-file"
                    size="sm"
                />
                <Stack
                    overflow="hidden"
                    gap="6px"
                >
                    <Flex
                        alignItems="center"
                        gap="8px"
                        overflow="hidden"
                    >
                        <StyledFileName>{file.fileName}</StyledFileName>
                        {isNew ? <Badge type="new" /> : null}
                        {isStalled || isFailed ? (
                            <StatusChip
                                id={statusId}
                                chipType={isStalled ? "warning" : "error"}
                                isLoading={isStalled}
                                ariaLive="polite"
                                dataAutomation="signal-recommender-file-status"
                            >
                                {translate(statusKey)}
                            </StatusChip>
                        ) : null}
                    </Flex>
                    <StyledFileDate>{formattedDate}</StyledFileDate>
                </Stack>
            </Flex>

            <Flex alignItems="center">
                <DealSignalFileMetric
                    value={isSucceeded ? file.companiesCount : undefined}
                    captionKey={signalRecommenderI18nKeys.list.companies}
                    emptyLabel={metricEmptyLabel}
                />
                <DealSignalFileMetric
                    value={isSucceeded ? file.signalsFoundCount : undefined}
                    captionKey={signalRecommenderI18nKeys.list.signalsFound}
                    emptyLabel={metricEmptyLabel}
                />

                <StyledMetaCell
                    alignItems="center"
                    justifyContent="center"
                >
                    <DealSignalFileAction
                        status={status}
                        isStalled={isStalled}
                        isDownloading={isDownloading}
                        isRefreshing={isRefreshing}
                        describedById={isSucceeded ? undefined : statusId}
                        onDownload={() => onDownload(file)}
                        onRefresh={() => onRefresh(file)}
                    />
                </StyledMetaCell>
            </Flex>
        </StyledFileRow>
    );
};
